import React from 'react';

/**
 * Consistent line-icon set for the header mega-menu category heads, keyed by
 * the (zh) category title. Rendered on both desktop and mobile menus so the
 * whole navigation shares one coherent icon language, independent of the
 * per-item images in Sanity (many categories have none). 24×24, 1.5 stroke,
 * currentColor — inherits the menu text colour.
 */

type IconProps = {className?: string};

const svg = (children: React.ReactNode) =>
  function Icon({className}: IconProps) {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden
      >
        {children}
      </svg>
    );
  };

const Helmet = svg(
  <>
    <path d="M3 13a9 9 0 0 1 18 0v1.5a1.5 1.5 0 0 1-1.5 1.5H10l-5.5-1A2 2 0 0 1 3 13Z" />
    <path d="M9 16v2.5a1.5 1.5 0 0 0 1.5 1.5H14" />
    <path d="M11 8h7.5" />
  </>,
);
const Apparel = svg(
  <>
    <path d="M9 3 5 5 3.5 9 6 10.5V20a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9.5L20.5 9 19 5l-4-2" />
    <path d="M9 3a3 3 0 0 0 6 0" />
  </>,
);
const Glove = svg(
  <>
    <path d="M7 11V6a1.5 1.5 0 0 1 3 0v4M10 10V4.5a1.5 1.5 0 0 1 3 0V10M13 10V6a1.5 1.5 0 0 1 3 0v6c0 4-2.5 8-6 8s-6-2-6-5c0-1.5 1-2.5 3-2.5" />
  </>,
);
const Bag = svg(
  <>
    <rect x="4" y="8" width="16" height="12" rx="2" />
    <path d="M8 8V6a4 4 0 0 1 8 0v2" />
  </>,
);
const Camera = svg(
  <>
    <rect x="3" y="7" width="18" height="12" rx="2" />
    <circle cx="12" cy="13" r="3" />
    <path d="M8 7l1.5-2h5L16 7" />
  </>,
);
const Sparkle = svg(
  <>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
    <path d="M18 15l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2Z" />
  </>,
);
const Exhaust = svg(
  <>
    <path d="M3 9h9l4 3h4a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-4l-4 3H4a1 1 0 0 1-1-1V9Z" />
    <path d="M7 12v3M10 12v3" />
  </>,
);
const Shield = svg(
  <>
    <path d="M12 3l7 2.5V11c0 5-3 8-7 10-4-2-7-5-7-10V5.5L12 3Z" />
    <path d="M9.5 12l2 2 3.5-4" />
  </>,
);
const Engine = svg(
  <>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 3v2.8M12 18.2V21M3 12h2.8M18.2 12H21M5.6 5.6l2 2M16.4 16.4l2 2M18.4 5.6l-2 2M7.6 16.4l-2 2" />
  </>,
);
const Steering = svg(
  <>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M12 4v5.5M6.3 17.7l4-3.4M17.7 17.7l-4-3.4" />
  </>,
);
const Brake = svg(
  <>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M12 4.2v2.5M12 17.3v2.5M4.2 12h2.5M17.3 12h2.5" />
  </>,
);
const Mirror = svg(
  <>
    <ellipse cx="14" cy="8" rx="5" ry="4" />
    <path d="M11 11.5 6 20" />
  </>,
);
const Rack = svg(
  <>
    <rect x="4" y="6" width="16" height="6" rx="1.5" />
    <path d="M6 12v6M18 12v6M6 18h12" />
  </>,
);
const Droplet = svg(
  <>
    <path d="M12 3s6 6.5 6 10.5A6 6 0 0 1 6 13.5C6 9.5 12 3 12 3Z" />
    <path d="M9.5 14a2.5 2.5 0 0 0 2.5 2.5" />
  </>,
);
const Battery = svg(
  <>
    <rect x="3" y="8" width="16" height="9" rx="2" />
    <path d="M19 11h2v3h-2" />
    <path d="M10 10.5 8.5 13H11l-1.5 2.5" />
  </>,
);
const Wheel = svg(
  <>
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="M12 3.5v5M12 15.5v5M3.5 12h5M15.5 12h5" />
  </>,
);
const Spray = svg(
  <>
    <rect x="7" y="9" width="7" height="11" rx="1.5" />
    <path d="M14 11h3l1 2M9 9V6h3V4h2M17 5h2M17 8h2" />
  </>,
);
const Lock = svg(
  <>
    <rect x="5" y="10" width="14" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </>,
);
const Wrench = svg(
  <>
    <path d="M15 7a4 4 0 0 1-5.2 5.4L5 17.2 6.8 19l4.8-4.8A4 4 0 0 0 17 9l-2.2 2.2-2-2L15 7Z" />
  </>,
);
const Pin = svg(
  <>
    <path d="M12 21c4-4 6-7 6-10a6 6 0 1 0-12 0c0 3 2 6 6 10Z" />
    <circle cx="12" cy="11" r="2.2" />
  </>,
);
const Route = svg(
  <>
    <circle cx="6" cy="7" r="2" />
    <circle cx="18" cy="17" r="2" />
    <path d="M8 7h6a3 3 0 0 1 0 6h-4a3 3 0 0 0 0 6h6" />
  </>,
);
const Tag = svg(
  <>
    <path d="M4 4h7l9 9-7 7-9-9V4Z" />
    <circle cx="8" cy="8" r="1.3" />
  </>,
);

/**
 * Match by keyword (zh + en, case-insensitive) so both storefront locales and
 * minor title variations resolve. Order matters — more specific first.
 */
const has =
  (...keys: string[]) =>
  (t: string) => {
    const s = t.toLowerCase();
    return keys.some((k) => s.includes(k.toLowerCase()));
  };

const RULES: {match: (t: string) => boolean; Icon: React.FC<IconProps>}[] = [
  {match: has('清潔', 'clean', 'care'), Icon: Spray},
  {match: has('頭盔', 'helmet'), Icon: Helmet},
  {match: has('服飾', '衣', 'apparel', 'jacket', 'cloth', 'wear'), Icon: Apparel},
  {match: has('手套', 'glove'), Icon: Glove},
  {match: has('包', 'bag', 'luggage'), Icon: Bag},
  {match: has('行車紀錄', '電子', '導航', 'camera', 'electronic', 'dashcam', 'navigation', 'gps'), Icon: Camera},
  {match: has('精品', 'accessor', 'gadget'), Icon: Sparkle},
  {match: has('排氣', 'exhaust'), Icon: Exhaust},
  {match: has('防摔', '保護', '車身及', 'protection', 'crash', 'guard'), Icon: Shield},
  {match: has('引擎', '傳動', '冷卻', 'engine', 'cooling', 'drivetrain', 'transmission'), Icon: Engine},
  {match: has('轉向', '腳踏', 'steering', 'footpeg', 'foot peg'), Icon: Steering},
  {match: has('煞車', '避震', 'brake', 'suspension'), Icon: Brake},
  {match: has('倒後鏡', '鏡', 'mirror'), Icon: Mirror},
  {match: has('尾架', 'rack', 'carrier'), Icon: Rack},
  {match: has('電池', 'battery'), Icon: Battery},
  {match: has('輪胎', '車輪', 'tire', 'tyre', 'wheel'), Icon: Wheel},
  {match: has('消耗', 'consumable', 'oil', 'fluid'), Icon: Droplet},
  {match: has('防盜', 'security', 'anti-theft', 'lock'), Icon: Lock},
  {match: has('工具', 'tool'), Icon: Wrench},
  {match: has('導航定位', 'pin'), Icon: Pin},
  {match: has('旅行', '活動', '歷奇', '越野', '道路', '運動', '復古', 'travel', 'tour', 'adventure', 'off-road', 'road', 'sport', 'retro'), Icon: Route},
  {match: has('車身', 'body'), Icon: Bag},
];

export function hasCategoryIcon(title?: string | null): boolean {
  return Boolean(title && RULES.some((r) => r.match(title)));
}

export default function CategoryIcon({
  title,
  className,
}: {
  title?: string | null;
  className?: string;
}) {
  const t = title ?? '';
  const rule = RULES.find((r) => r.match(t));
  const Icon = rule?.Icon ?? Tag;
  return <Icon className={className} />;
}
